// src/components/common/SelectDropdown.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * CustomDropdown (fully stylable)
 * Props:
 *  - label (string)
 *  - options: array of { value, label } or strings
 *  - value: selected value
 *  - onChange: function(e) where e.target.value is selected value (to match native API)
 *  - placeholder: string
 *  - id: string
 *  - className: additional classes
 *
 * Accessibility:
 *  - button with aria-haspopup & aria-expanded
 *  - listbox with role="listbox", each item role="option" and aria-selected
 *
 * Features:
 *  - slide down animation on open
 *  - scrollable list when tall
 *  - keyboard navigation & typeahead
 */
export default function SelectDropdown({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Choose...",
  id,
  className = "",
  error,
}) {
  // normalize options
  const opts = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [queryBuffer, setQueryBuffer] = useState("");
  const bufferTimerRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Measure space on open
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // If less than 280px (approx max height) below, and more space above, flip it
      if (spaceBelow < 280 && rect.top > 280) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }
    }
  }, [open]);

  // find index for current value
  const selectedIndex = opts.findIndex((o) => o.value === value);

  // close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // reset highlight when opening
  useEffect(() => {
    if (open) {
      setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
      // ensure highlighted item visible after a tick
      setTimeout(() => {
        scrollToHighlighted();
      }, 120);
    } else {
      setHighlight(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // scroll list to highlighted item
  function scrollToHighlighted() {
    if (!listRef.current || highlight < 0) return;
    const item = listRef.current.querySelector(`[data-index="${highlight}"]`);
    if (item) {
      const listRect = listRef.current.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.top < listRect.top) {
        item.scrollIntoView({ block: "nearest" });
      } else if (itemRect.bottom > listRect.bottom) {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }

  // keyboard handling: arrows, enter, escape; typeahead
  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => {
        const next = h < opts.length - 1 ? h + 1 : 0;
        return next;
      });
      setTimeout(scrollToHighlighted, 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => {
        const next = h > 0 ? h - 1 : opts.length - 1;
        return next;
      });
      setTimeout(scrollToHighlighted, 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && highlight >= 0 && opts[highlight]) {
        selectOption(opts[highlight]);
      } else {
        setOpen((s) => !s);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key.length === 1 && e.key.match(/\S/)) {
      // typeahead: accumulate buffer
      const ch = e.key.toLowerCase();
      setQueryBuffer((prev) => prev + ch);
      // clear previous timer
      if (bufferTimerRef.current) clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = setTimeout(() => {
        setQueryBuffer("");
        bufferTimerRef.current = null;
      }, 700);

      const search = (queryBuffer + ch).toLowerCase();
      const matchIndex = opts.findIndex((o) =>
        (o.label + "").toLowerCase().startsWith(search)
      );
      if (matchIndex >= 0) {
        if (!open) setOpen(true);
        setHighlight(matchIndex);
        setTimeout(scrollToHighlighted, 0);
      }
    }
  }

  function toggleOpen() {
    setOpen((s) => !s);
  }

  function selectOption(opt) {
    setOpen(false);
    onChange && onChange({ target: { value: opt.value } });
  }

  const selectedLabel = (opts.find((o) => o.value === value) || {}).label;

  return (
    <div className={className} style={{ marginBottom: 12, maxWidth: 420 }}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label.replace(' *', '')}
          {label.includes('*') && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        ref={containerRef}
        className="custom-dropdown"
        tabIndex={0}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        role="combobox"
        aria-owns={id ? `${id}-listbox` : undefined}
        aria-controls={id ? `${id}-listbox` : undefined}
      >
        <button
          type="button"
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`select-field dropdown-button dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
            error ? "border-red-500 ring-1 ring-red-500" : ""
          }`}
          id={id}
        >
          <span className="truncate" style={{ marginRight: 8 }}>
            {selectedLabel || placeholder}
          </span>
          <span
            className={`select-arrow ${open ? "open" : ""} dark:border-gray-400`}
            aria-hidden="true"
          />
        </button>

        {open && (
          <div
            ref={listRef}
            id={id ? `${id}-listbox` : undefined}
            role="listbox"
            tabIndex={-1}
            className={`dropdown-popover ${dropUp ? "bottom-full mb-2 origin-bottom" : "slide-down"} dark:bg-gray-800 dark:border-gray-700`}
            style={dropUp ? { marginTop: 0 } : {}}
            aria-activedescendant={
              highlight >= 0
                ? `${id ? id + "-option-" : "opt-"}${highlight}`
                : undefined
            }
          >
            {opts.length === 0 ? (
              <div
                className="dropdown-empty"
                style={{ padding: 12, color: "#6b7280" }}
              >
                No options
              </div>
            ) : (
              opts.map((o, i) => (
                <div
                  key={o.value}
                  id={id ? `${id}-option-${i}` : undefined}
                  data-index={i}
                  role="option"
                  aria-selected={value === o.value}
                  className={`dropdown-item dark:text-gray-200 ${
                    i === highlight
                      ? "highlighted bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(ev) => {
                    // use onMouseDown to avoid losing focus before click
                    ev.preventDefault();
                    selectOption(o);
                  }}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
